// Labels and value formatting shared by the sync review pop-up and the CAB page's inline review. They live in a .ts
// file so the .tsx files export only components (react-refresh/only-export-components).
import type { BadgeTone } from '../Badge';
import type { SyncProposalAction } from '../../services/requirementSyncApi';

export const ACTION_BADGE: Record<SyncProposalAction, { label: string; tone: BadgeTone }> = {
  update: { label: 'Update', tone: 'brand' },
  create: { label: 'New', tone: 'success' },
  delete: { label: 'Delete', tone: 'error' },
};

export const TICKET_FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  description: 'Description',
  work_type: 'Work type',
  implementation_steps: 'Implementation steps',
};

/** "acceptanceCriteria" -> "Acceptance criteria". */
export function fieldLabel(field: string): string {
  if (TICKET_FIELD_LABELS[field]) return TICKET_FIELD_LABELS[field];
  const words = field.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export const OUTCOME_TEXT: Record<string, string> = {
  carried: 'Carried into the CAB',
  already_stated: 'Already in the CAB',
  not_requirement: 'Not a requirement change',
  question: 'Recorded as an open question',
  no_change: 'Nothing to change',
  previously_rejected: 'Rejected in an earlier review',
  unhandled: 'Not worked out yet — stays pending',
};

export interface DiffPart {
  text: string;
  changed: boolean;
}

// Past this many word pairs, the differing middle of two texts is marked changed as a whole.
const MAX_DIFF_CELLS = 1_000_000;

/** The words of a Before and an After text, each marked changed or not: what was removed in ``before``, what was
 *  added in ``after``. Words are compared as written, spaces included, so formatting stays as it was. */
export function diffWords(oldText: string, newText: string): { before: DiffPart[]; after: DiffPart[] } {
  const a = oldText.match(/\s+|\S+/g) ?? [];
  const b = newText.match(/\s+|\S+/g) ?? [];
  // Most of a description is the same: compare only what lies between the shared start and end.
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) { endA--; endB--; }
  const keptA = a.map((_, i) => i < start || i >= endA);
  const keptB = b.map((_, j) => j < start || j >= endB);
  const n = endA - start;
  const m = endB - start;
  if (n && m && n * m <= MAX_DIFF_CELLS) {
    // Longest common subsequence of the middles; len[i][j] covers a[start+i..] and b[start+j..].
    const w = m + 1;
    const len = new Uint32Array((n + 1) * w);
    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        len[i * w + j] = a[start + i] === b[start + j]
          ? len[(i + 1) * w + j + 1] + 1
          : Math.max(len[(i + 1) * w + j], len[i * w + j + 1]);
      }
    }
    for (let i = 0, j = 0; i < n && j < m;) {
      if (a[start + i] === b[start + j]) {
        keptA[start + i++] = true;
        keptB[start + j++] = true;
      } else if (len[(i + 1) * w + j] >= len[i * w + j + 1]) {
        i++;
      } else {
        j++;
      }
    }
  }
  return { before: toParts(a, keptA), after: toParts(b, keptB) };
}

/** Joins words into runs; a space between two changed words counts as changed, so "High, Urgent" is one mark. */
function toParts(tokens: string[], kept: boolean[]): DiffPart[] {
  const parts: DiffPart[] = [];
  tokens.forEach((text, i) => {
    const changed = !kept[i] || (/^\s+$/.test(text) && i > 0 && i < tokens.length - 1 && !kept[i - 1] && !kept[i + 1]);
    const last = parts[parts.length - 1];
    if (last && last.changed === changed) last.text += text;
    else parts.push({ text, changed });
  });
  return parts;
}

export function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value
      .map((v, i) => {
        if (v && typeof v === 'object' && 'step' in v) {
          const step = v as { step: string; human_required?: boolean };
          return `${i + 1}. ${step.step}${step.human_required ? ' (needs a person)' : ''}`;
        }
        if (v && typeof v === 'object') {
          return Object.entries(v as Record<string, unknown>)
            .map(([k, x], j) => `${j === 0 ? '- ' : '  '}${fieldLabel(k)}: ${typeof x === 'string' ? x : JSON.stringify(x)}`)
            .join('\n');
        }
        return typeof v === 'string' ? `- ${v}` : JSON.stringify(v);
      })
      .join('\n');
  }
  return JSON.stringify(value, null, 2);
}
